import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AddTaskData, AddTaskMember, EditTaskDate, EditTaskInformation, ProjectStatistics,
    Task, TaskDetail, TaskHardwareReservationIn, TaskMember, TaskMemberCandidate,
    TaskParams, TaskPrime, TransferTaskPrime,
} from '@app/interfaces/lm/task';
import { Option, SelectOption } from '@app/interfaces/sys/dict';
import { environment } from '@env/environment';
import { lastValueFrom } from 'rxjs';
import { ProjectTrackerTask } from '@app/interfaces/lm/kanban-board';
import dayjs from 'dayjs';
import { J } from '@fullcalendar/core/internal-common';

@Injectable({
    providedIn: 'root',
})
export class TaskService {
    private moduleApi = environment.api.services.lm;
    private baseUrl = this.moduleApi + '/task';

    private priorityOptions: Option[] = [
        {
            id: 1,
            name: 'Critical',
        },
        {
            id: 2,
            name: 'High',
        },
        {
            id: 3,
            name: 'Medium',
        },
        {
            id: 4,
            name: 'Low',
        },
    ];

    constructor(private http: HttpClient) {
    }

    async find(params: TaskParams): Promise<Task[]> {
        return lastValueFrom(this.http.get<Task[]>(`${this.baseUrl}/list`, {
            params: Object.assign(params),
        }));
    }

    async statistics(params: { projectId: string }): Promise<ProjectStatistics> {
        return lastValueFrom(this.http.get<ProjectStatistics>(`${this.baseUrl}/statistics`, {
            params: Object.assign(params),
        }));
    }

    async detail(id: string): Promise<TaskDetail> {
        return lastValueFrom(this.http.get<TaskDetail>(`${this.baseUrl}/details`, {
            params: Object.assign({
                task: id,
            }),
        }));
    }

    async add(data: AddTaskData): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/create`, data));
    }

    async remove(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/delete/${id}`));
    }

    async edit(data: AddTaskData): Promise<Task> {
        return lastValueFrom(this.http.put<Task>(`${this.baseUrl}`, data));
    }

    async editInformation(data: EditTaskInformation): Promise<Task> {
        return lastValueFrom(this.http.post<Task>(`${this.baseUrl}/edit/information`, data));
    }

    async findPrime(params: { task: string }): Promise<TaskPrime[]> {
        return lastValueFrom(this.http.get<TaskPrime[]>(`${this.baseUrl}/prime/`, {
            params: Object.assign(params),
        }));
    }

    async findPrimeCandidates(params: { task?: string }): Promise<TaskPrime[]> {
        return lastValueFrom(this.http.get<TaskPrime[]>(`${this.baseUrl}/prime/candidates`, {
            params: Object.assign(params),
        }));
    }

    async transferTaskPrime(data: TransferTaskPrime): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/prime/transfer`, data));
    }

    async complete(params: { task: string }): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/status/complete`, {}, {
            params: Object.assign(params),
        }));
    }

    async cancel(params: { task: string }): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/status/cancel`, {}, {
            params: Object.assign(params),
        }));
    }

    async pause(params: { taskId: string, keepResources: string[] }): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/status/pause`, params));
    }

    async reactivate(params: { taskId: string, dueTime: number }): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/status/reactivate`, params));
    }

    async fetchTaskStatus(): Promise<SelectOption[]> {
        return new Promise(resolve => {
            const data: SelectOption[] = [
                {
                    label: 'All',
                    value: '',
                },
                {
                    label: 'Planned',
                    value: 'PLANNED',
                },
                {
                    label: 'In Progress',
                    value: 'IN_PROGRESS',
                },
                {
                    label: 'Done',
                    value: 'DONE',
                },
                {
                    label: 'Cancelled',
                    value: 'CANCELLED',
                },
                {
                    label: 'Overdue',
                    value: 'OVERDUE',
                },
                {
                    label: 'Paused',
                    value: 'PAUSED',
                },
            ];
            resolve(data);
        });
    }

    async findMembers(params: { task: string }): Promise<TaskMember[]> {
        return lastValueFrom(this.http.get<TaskMember[]>(`${this.baseUrl}/member/list`, {
            params: Object.assign(params),
        }));
    }

    async findMembersByProject(params: { project: string }): Promise<TaskMember[]> {
        return lastValueFrom(this.http.get<TaskMember[]>(`${this.baseUrl}/member/list/by/project`, {
            params: Object.assign(params),
        }));
    }

    async findMemberCandidates(params: { task: string }): Promise<TaskMemberCandidate[]> {
        return lastValueFrom(this.http.get<TaskMemberCandidate[]>(`${this.baseUrl}/member/candidates`, {
            params: Object.assign(params),
        }));
    }

    async addMember(data: { task: string, accounts: string[] }): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/member/add`, data));
    }

    async removeMember(params: { task: string, taskMembers: string[] }): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/member/delete`, {
            params: Object.assign(params),
        }));
    }

    async editDate(data: EditTaskDate): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/schedule/adjust`, data));
    }

    async findMyTask(params?: { status?: string }): Promise<Task[]> {
        return lastValueFrom(this.http.get<Task[]>(`${this.baseUrl}/list/my`, params ? {
            params: params ? Object.assign(params) : null,
        } : {}));
    }

    async findHardwareReservation(params: { hardwareReservationIn: string }): Promise<TaskHardwareReservationIn[]> {
        return lastValueFrom(this.http.get<TaskHardwareReservationIn[]>(`${this.baseUrl}/list/by/hardwareReservationIn`, {
            params: Object.assign(params),
        }));
    }

    async findHumanReservation(params: { humanReservationIn: string[] }): Promise<TaskHardwareReservationIn[]> {
        return lastValueFrom(this.http.get<TaskHardwareReservationIn[]>(`${this.baseUrl}/list/by/humanReservationIn`, {
            params: Object.assign(params),
        }));
    }

    async findPriorityOptions(): Promise<Option[]> {
        return new Promise(resolve => {
            resolve(this.priorityOptions);
        });
    }

    formatPriority(value: number): string {
        if (!value) {
            return '';
        }
        const option = this.priorityOptions.find(item => item.id === value);
        return option ? option.name : '';
    }

    taskSortStartTime(data: Task[], type: string = 'table'): Task[] {
        data?.sort((obj1: Task, obj2: Task): number => {
            return type === 'table' ? obj1.startTime - obj2.startTime : obj2.startTime - obj1.startTime;
        });
        return data;
    }
}
